import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ModeToggle } from "@/components/mode-toggle";
import { FaGoogle, FaApple } from 'react-icons/fa';
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import Logo from "@/components/logo";
import { toast } from "sonner";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const formSchema = z.object({
  email: z.string().email({
    message: "Please enter a valid email address.",
  }),
});

const Login = () => {

    const navigate = useNavigate();
    const { axiosInstance } = useAuth();

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [showWaitlistModal, setShowWaitlistModal] = useState(false);

    const [waitlistEmail, setWaitlistEmail] = useState("");

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            email: "",
        },
    });

    const onSubmit = async (values) => {
        try {
            const { email } = values;
            setIsSubmitting(true);
            const response = await axiosInstance.post('/auth/magic-link', { email });

            if (response.data.existingUserOrAllowNewUsers) {
                navigate(`/verify-email/${response.data.tokenId}`);
            } else {
                setWaitlistEmail(email);
                localStorage.setItem('waitlistEmail', email);
                setShowWaitlistModal(true);
            }

        } catch (error) {
            console.error(error);
            form.setError('email', {
                type: 'manual',
                message: 'Failed to send magic link. Please try again.',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGoogleLogin = () => {
        toast.error("Google login is currently disabled.");
    };

    const handleAppleLogin = () => {
        toast.error("Apple login is currently disabled.");
    };

    return (
        <>
        <div className="flex flex-col-reverse md:flex-row min-h-screen">
            {/* Left side */}
            <div className="w-full md:w-2/5 hidden md:flex flex-col p-4 bg-zinc-900 md:border-r md:border-zinc-800">
                <Logo variant="dark" onClick={() => { navigate('/') }} />
                {/* Customer quote */}
                {/* <div className="mt-auto text-sm md:text-base text-muted-foreground">
                    <blockquote className="italic">
                        "Status.Bot's AI-driven alerts have revolutionized our incident response. It's like having a 24/7 expert team monitoring our services!"
                    </blockquote>
                    <p className="mt-2">Sarah Johnson, CTO at TechNova Solutions</p>
                </div> */}
            </div>

            {/* Right side */}
            <div className="w-full md:w-3/5 bg-background flex items-center justify-center p-8 md:p-12 flex-grow md:flex-grow-0 relative">
                <div className="absolute top-4 left-4 md:hidden">
                    <Logo onClick={() => { navigate('/') }} />
                </div>
                <div className="absolute top-4 right-4">
                    <ModeToggle />
                </div>
                <div className="w-full max-w-md space-y-8">
                    <h2 className="text-3xl font-bold text-center">Let's get started</h2>
                    <div className="space-y-6">
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                                <FormField
                                    control={form.control}
                                    name="email"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormControl>
                                                <Input autoFocus placeholder="Company email" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <Button type="submit" className="w-full">
                                    {isSubmitting ? 'Sending...' : 'Send Magic Link'}
                                </Button>
                            </form>
                        </Form>
                        {/* <div className="relative py-4">
                            <div className="absolute inset-0 flex items-center">
                                <span className="w-full border-t" />
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="bg-background px-4 text-muted-foreground">Or</span>
                            </div>
                        </div> */}
                        {/* <Button variant="outline" className="w-full flex items-center justify-center gap-3" onClick={handleGoogleLogin}>
                            <FaGoogle className="text-lg" /> Sign in with Google
                        </Button>
                        <Button variant="outline" className="w-full flex items-center justify-center gap-3" onClick={handleAppleLogin}>
                            <FaApple className="text-2xl" /> Sign in with Apple
                        </Button> */}
                        <p className="text-xs text-center text-muted-foreground mt-6">
                            By signing in, you agree to our{' '}
                            <Link to="/terms" className="underline hover:text-primary">Terms of Service</Link>{' '}
                            and{' '}
                            <Link to="/privacy" className="underline hover:text-primary">Privacy Policy</Link>.
                        </p>
                    </div>
                </div>
            </div>
        </div>
            <Dialog open={showWaitlistModal} onOpenChange={setShowWaitlistModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold mb-2">
                            Thanks for the interest!
                        </DialogTitle>
                        <DialogDescription className="mb-4">
                            <div className="space-y-2">
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                    We couldn't find an existing account for{" "}
                                    <span className="text-primary font-semibold">
                                        {waitlistEmail}
                                    </span>.
                                </p>
                                <p className="text-sm text-muted-foreground leading-relaxed">
                                    Be the first in line—sign up for our waitlist for early access to StatusBot as soon as it opens.
                                </p>
                            </div>
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button className="w-1/2" variant="outline" onClick={() => setShowWaitlistModal(false)}>
                            Cancel
                        </Button>
                        <Button className="w-1/2 p-0">
                            <Link className="w-full p-4" to="/waitlist">
                                Sign up
                            </Link>
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default Login;

